import { ArrowLeft, ArrowRight, Mail, TrendingDown, TrendingUp } from 'lucide-react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { Button } from '@/components/ui/Button'
import { CaseStatusPill } from '@/components/ui/StatusPill'
import { cn } from '@/lib/cn'
import { useFormat } from '@/i18n/useFormat'
import { placeName } from '@/lib/countries'
import { profitSign } from '@/features/cases/lib/profit'
import { formatNumber } from '@/lib/format'
import { MODE_ICONS, modeLabelKey } from '@/lib/icons'
import type { CaseDetail } from '@/types/domain'

/** One figure in the sticky summary band. */
function Figure({
  label,
  caption,
  value,
  unit,
  tone = 'default',
  /** Tabular figures are for MONEY, which is scanned as a column. A date or a
   *  mode is read as words, and tabular padding pulls "4 Aug 2026" apart. */
  numeric = false,
}: {
  label: string
  /** A quiet qualifier after the label — «Прибыль · прогноз». Lowercase and
   *  light, so the label still reads first. */
  caption?: string
  value: string
  unit?: string
  tone?: 'default' | 'negative' | 'positive'
  numeric?: boolean
}) {
  const TrendIcon =
    tone === 'negative' ? TrendingDown : tone === 'positive' ? TrendingUp : null

  return (
    /* Cells are separated by hairlines rather than bare gaps, so the band
       reads as a row of facts instead of a run-on sentence. */
    <div className="border-line flex shrink-0 flex-col gap-1.5 border-l pr-5 pl-5 first:border-l-0 first:pl-0 last:pr-0 sm:pr-7 sm:pl-7">
      <span className="asa-eyebrow whitespace-nowrap">
        {label}
        {caption ? (
          <span className="text-text-faint font-light tracking-normal normal-case">
            {' · '}
            {caption}
          </span>
        ) : null}
      </span>
      <span
        className={cn(
          'flex items-baseline gap-1.5 text-[17px] leading-none font-semibold tracking-[-0.2px] whitespace-nowrap',
          numeric && 'asa-num',
          tone === 'negative'
            ? 'text-negative'
            : tone === 'positive'
              ? 'text-positive'
              : 'text-text-heading',
        )}
      >
        {/* The sign is already in the number; the icon restates direction so
            the colour is never the only carrier. */}
        {TrendIcon ? <TrendIcon size={14} className="self-center" aria-hidden /> : null}
        {value}
        {unit ? (
          <span className="text-text-muted text-[11px] font-normal tracking-normal">
            {unit}
          </span>
        ) : null}
      </span>
    </div>
  )
}

/* The sticky summary header: number, client, status, rate, cost, profit.
 * It stays on screen through every section because those six facts are what
 * every question about the case is asked against. Two zones: an identity row,
 * and a metrics band separated by tone (sunken surface), not shadow. */
export function CaseHeader({
  record,
  onSendStatus,
  lifecycle,
  chatBadge,
  more,
}: {
  record: CaseDetail
  /** Opens the send-status sheet (FR-41). */
  onSendStatus: () => void
  /** The lifecycle controls (FR-25, AD-24) — the moves this case may still
   *  make, and the audited reopen.
   *
   *  A NODE rather than a status and a callback, for the same reason the chat
   *  badge is one: this component draws facts and owns no mutation, and a
   *  header that both rendered the status pill and posted the change would
   *  drag three writes, a session read and a confirmation dialog into every
   *  screen that wants a case's number on it. What belongs here is the
   *  PLACEMENT — see the identity row below for why it sits where it does. */
  lifecycle?: ReactNode
  /** The phone's way into the conversation (Story 8.3, UX-DR-28), rendered by
   *  the page so this component stays ignorant of chat state.
   *
   *  It belongs in the STICKY header specifically, which is the AC's word: the
   *  header is the one piece of chrome that survives every section switch and
   *  every scroll, so the badge is reachable from wherever a phone user
   *  happens to be in the record. It hides itself above lg, where the
   *  persistent column makes a way in unnecessary. */
  chatBadge?: ReactNode
  /** The «⋯» menu (S-11): what is about the case rather than the work on it —
   *  its history, which left the section rail for this. A node for the reason
   *  `lifecycle` is one: the page owns the sheet it opens. */
  more?: ReactNode
}) {
  const { t, i18n } = useTranslation()
  const format = useFormat()
  /* Undefined for a case nobody stated a mode for (a NULL column), and it
     must stay undefined rather than fall back: rendering `undefined` as a
     component is a React crash, and a fallback icon is a claim about the
     shipment nobody made. */
  const ModeIcon = record.mode ? MODE_ICONS[record.mode] : undefined
  const money = record.money
  /* The ONE profit (S-6): the figure the server chose by the case's status,
     the same as the right side of the money line. Zero is neutral — every
     uncosted case used to wear the green arrow of a profitable one. */
  const profit = money?.profit
  const sign = profitSign(profit?.amount)
  const profitTone = sign === 'loss' ? 'negative' : sign === 'gain' ? 'positive' : 'default'

  return (
    /* Bled to both page gutters so the sticky bar spans the full content
       column — the chat is a floating overlay, so nothing is reserved beside
       it and the header carries identity and figures only. */
    <div className="bg-surface/85 border-border-subtle sticky top-0 z-20 -mx-4 border-b px-4 pt-3.5 pb-4 backdrop-blur-lg sm:-mx-6 sm:px-6">
      <div className="mx-auto flex w-full max-w-[var(--spacing-page-max)] flex-col gap-3">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <Link
            to="/cases"
            aria-label={t('cases.backToList')}
            className="text-text-muted hover:text-text-heading hover:bg-surface-hover border-line -ml-1 flex size-7 shrink-0 items-center justify-center rounded-md border transition-colors duration-[var(--dur-fast)] hover:no-underline"
          >
            <ArrowLeft size={15} aria-hidden />
          </Link>

          <h1 className="text-text-heading text-[20px] leading-none font-semibold tracking-[-0.5px]">
            {record.number}
          </h1>
          <CaseStatusPill status={record.status} />

          <span className="text-text-body text-[13px]">{record.clientName}</span>

          {/* The lane as one object — mode, origin, destination — instead of a
              run-on sentence after the client name. */}
          <span className="border-line text-text-body flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[12.5px] leading-none">
            {ModeIcon ? (
              <ModeIcon size={13} className="text-text-muted" aria-hidden />
            ) : null}
            {/* The stored value is an ISO 3166-1 code on a case opened through
                the sheet and a city on one migrated from the workbook;
                `placeName` reads both, and never prints a bare "KZ". */}
            {placeName(record.origin, i18n.language)}
            <ArrowRight size={12} className="text-text-faint" aria-hidden />
            {placeName(record.destination, i18n.language)}
          </span>

          <span className="flex-1" />

          {record.quoteNumber ? (
            <Link
              to={`/quotes/${record.quoteId}`}
              className="text-[12px] whitespace-nowrap"
            >
              {t('cases.actions.fromQuote')} {record.quoteNumber}
            </Link>
          ) : null}

          {/* Where the case GOES next, beside the status that says where it
              is: the pill at the head of this row and these controls are two
              halves of one sentence, and putting the moves in a section
              further down would mean scrolling away from the fact they act on.
              Before the send action rather than after it, because moving a
              case is a change to the record and mailing about it is not —
              actions on the record come first, actions that leave the system
              come last. */}
          {lifecycle}

          {/* Telling the client where their shipment is (FR-41).
              Secondary, not primary: Gateway Blue is scarce and belongs to the
              one action a screen is FOR, and this page is for reading a case
              rather than for mailing about it. It carries a text label because
              the action leaves the system — the design allows an icon alone only
              where nothing is destroyed, and an email cannot be unsent. */}
          <Button
            variant="secondary"
            size="sm"
            iconLeft={Mail}
            onClick={onSendStatus}
            className="shrink-0"
          >
            {t('cases.actions.sendStatus')}
          </Button>

          {/* Last in the row, and after the send action rather than before it:
              on a phone this is the control the thumb reaches for most often on
              a case being coordinated, and the trailing edge is where it lands. */}
          {chatBadge}

          {/* The very end of the row, on every breakpoint: the overflow of a
              record's header is where its rarely-needed views live, and a
              trailing «⋯» is where a reader looks for them (as on the quote). */}
          {more}
        </div>

        {/* Wrap, never scroll: a scrollbar inside a sticky header is chrome the
            eye trips over every time it passes, and a clipped label reads as a
            broken page. A second row of figures costs height only on screens
            that actually need it. */}
        <div className="border-line bg-surface-sunken flex flex-wrap gap-y-3.5 rounded-lg border px-4 py-3 sm:px-5">
          <Figure label="ETD" value={format.date(record.etd)} />
          <Figure label="ETA" value={format.date(record.eta)} />
          {money ? (
            <>
              <Figure
                label={t('cases.header.rate')}
                numeric
                value={formatNumber(money.rate.amount, 2)}
                unit={money.rate.currency}
              />
              {/* `actualCost` is the FORECAST cost — the actual figure where a
                  line has one, the planned figure where it does not — so it
                  names that basis the way the profit beside it does. Always
                  «прогноз», whatever the profit's basis: the sum is the same
                  shape on a closed case. Without it the reader compares the
                  figure with the expense table's actual column and finds a
                  different number. */}
              <Figure
                label={t('cases.header.cost')}
                caption={t('cases.header.costBasis')}
                numeric
                value={formatNumber(money.actualCost.amount, 2)}
                unit={money.actualCost.currency}
              />
              <Figure
                label={t('cases.header.profit')}
                caption={
                  money.profitBasis ? t(`cases.money.profit.${money.profitBasis}`) : undefined
                }
                numeric
                value={formatNumber(profit?.amount, 2)}
                unit={profit?.currency}
                tone={profitTone}
              />
            </>
          ) : null}
          {/* A dash for no mode, like the dates beside it: the band's slots are
              fixed, and an absent fact reads as one rather than as a gap. */}
          <Figure
            label={t('cases.header.mode')}
            value={record.mode ? t(modeLabelKey(record.mode)) : '—'}
          />
        </div>
      </div>
    </div>
  )
}
